import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Link, Text } from '@chakra-ui/react';

import { CUSTOMER_SUPPORT_URL } from './constants';
import { VerificationCodeStep } from './VerificationCodeStep';
import { VerificationMethodStep } from './VerificationMethodStep';
import { EVerificationMethod } from './VerificationMethodStep/types';
import { useAuth } from '../../Auth';
import { EColor, ETextVariant } from '../../Theme';
import { EVerificationStep } from '../context';
import { useVerificationStepper } from '../hooks.ts';

interface ITwoFactorAuthContent {
  monolithBaseUrl: string;
  onSuccess: (...args: unknown[]) => void;
}

export const TwoFactorAuthContent: FC<ITwoFactorAuthContent> = ({
  monolithBaseUrl,
  onSuccess,
}) => {
  const { profile } = useAuth() || {};
  const { step } = useVerificationStepper();
  const isVerificationComplete =
    step === EVerificationStep.VERIFICATION_COMPLETE;

  useEffect(() => {
    if (isVerificationComplete) {
      const timeout = setTimeout(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const redirectUrl = searchParams.get('redirectUrl');

        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          onSuccess();
        }
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [isVerificationComplete, onSuccess]);

  const { email, phone } = useMemo(
    () => ({
      email: profile?.email,
      phone: profile?.phone ?? profile?.userAddress?.phone,
    }),
    [profile]
  );

  const getDefaultVerificationMethod = () => {
    if (phone) return EVerificationMethod.PHONE;
    if (!phone && email) return EVerificationMethod.EMAIL;
    return null;
  };

  const [activeMethod, setActiveMethod] = useState<EVerificationMethod | null>(
    getDefaultVerificationMethod()
  );

  const changeMethod = useCallback(
    (method: EVerificationMethod) => {
      if (method !== activeMethod) {
        setActiveMethod(method);
      }
    },
    [activeMethod]
  );
  return phone || email ? (
    <Box>
      {step === EVerificationStep.VERIFICATION_METHOD && (
        <VerificationMethodStep
          changeMethod={changeMethod}
          email={email}
          monolithBaseUrl={monolithBaseUrl}
          phone={phone}
        />
      )}

      {(step === EVerificationStep.VERIFICATION_CODE ||
        isVerificationComplete) && (
        <VerificationCodeStep
          activeMethod={activeMethod}
          email={email}
          monolithBaseUrl={monolithBaseUrl}
          phone={phone}
        />
      )}
    </Box>
  ) : (
    <Text
      as="h1"
      fontSize={{ base: '18px', md: '22px' }}
      marginBottom={0}
      textAlign="center"
      variant={ETextVariant.BaseParagraph}
    >
      Something went wrong. Please reach out to Customer Service at{' '}
      <Link
        isExternal
        color={EColor.MediumBlue}
        fontWeight="700"
        href={CUSTOMER_SUPPORT_URL}
      >
        cs@fanaticscollect.com
      </Link>
    </Text>
  );
};
